/**
 * Octo AI uçları (Octo.Dal: AiChatController + ClaudeCodeController).
 *
 * Uçların tamamı hazırdı — masaüstü kabuğu (Blazor `AiChatPanel`) ve mobil uygulama
 * aynılarını kullanıyor; burada yeni uç yazılmadı.
 *
 * Yetki üç ayrı şeye bağlı ve üçü de ayrı ayrı sorulmalı:
 *   1. Kullanıcının AI sohbet yetkisi (`user.canUseAiChat`, giriş yanıtından gelir),
 *   2. Sunucuda Claude Code'un çalışır olması (`/api/claudecode/status`),
 *   3. Token kotası (merkezî havuz, CustomerProductId bazında).
 * Üçünden biri eksikken paneli açmak, kullanıcıyı ancak mesaj gönderdiğinde öğrendiği
 * bir duvara götürüyordu.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/shared/api/http/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import type {
  AiAttachmentRef,
  AiConversation,
  AiDataSource,
  AiDataSourceSaveRequest,
  AiDataSourceTestResponse,
  AiInstruction,
  AiKnowledgeDocument,
  AiKnowledgeDocumentSaveRequest,
  AiMessageDto,
  AiQuota,
  AiRenderResponse,
  AiReportSummary,
  AiStatus,
} from '@/shared/types/aiChat';

export const aiKeys = {
  status: ['ai', 'status'] as const,
  quota: (userId: string) => ['ai', 'quota', userId] as const,
  conversations: (userId: string) => ['ai', 'conversations', userId] as const,
  messages: (conversationId: string) => ['ai', 'messages', conversationId] as const,
  instructions: ['ai', 'instructions'] as const,
  reports: (userId: string) => ['ai', 'reports', userId] as const,
  report: (reportId: string) => ['ai', 'report', reportId] as const,
  dataSources: ['ai', 'datasources'] as const,
  knowledge: ['ai', 'knowledge'] as const,
  knowledgeDoc: (documentId: string) => ['ai', 'knowledge', documentId] as const,
};

// ---------- Octo AI Ayarları: bağlantılar, bilgi dokümanları, skill'ler ----------

/** Ek SQL bağlantıları; besteci seçicisi (yalnız aktifler) ve yönetim ekranı (tümü) aynı sorguyu okur. */
export function useAiDataSources(enabled = true) {
  return useQuery({
    queryKey: aiKeys.dataSources,
    queryFn: () => apiClient.get<AiDataSource[]>(ENDPOINTS.AI_DATASOURCES, { octo: { expectSuccess: false } }),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useSaveAiDataSource(userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AiDataSourceSaveRequest) =>
      apiClient.post<AiDataSource>(ENDPOINTS.AI_DATASOURCE_SAVE(userId ?? ''), body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiKeys.dataSources });
      void qc.invalidateQueries({ queryKey: aiKeys.knowledge });
    },
  });
}

export function useDeleteAiDataSource(userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dataSourceId: string) => apiClient.delete(ENDPOINTS.AI_DATASOURCE_DELETE(dataSourceId, userId ?? '')),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiKeys.dataSources });
      void qc.invalidateQueries({ queryKey: aiKeys.knowledge });
    },
  });
}

/** Bağlantı testi: dize verilirse o, verilmezse kayıtlı kaynağınki denenir. */
export function testAiDataSource(userId: string, body: { connectionString?: string | null; dataSourceId?: string | null }) {
  return apiClient.post<AiDataSourceTestResponse>(ENDPOINTS.AI_DATASOURCE_TEST(userId), body, {
    octo: { expectSuccess: false },
  });
}

export function useAiKnowledgeDocuments(enabled = true) {
  return useQuery({
    queryKey: aiKeys.knowledge,
    queryFn: () => apiClient.get<AiKnowledgeDocument[]>(ENDPOINTS.AI_KNOWLEDGE, { octo: { expectSuccess: false } }),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Tek doküman, içerikle (düzenleme için). */
export function fetchAiKnowledgeDocument(documentId: string) {
  return apiClient.get<AiKnowledgeDocument>(ENDPOINTS.AI_KNOWLEDGE_DOC(documentId, true), {
    octo: { expectSuccess: false },
  });
}

export function useSaveAiKnowledgeDocument(userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AiKnowledgeDocumentSaveRequest) =>
      apiClient.post<AiKnowledgeDocument>(ENDPOINTS.AI_KNOWLEDGE_SAVE(userId ?? ''), body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiKeys.knowledge });
      void qc.invalidateQueries({ queryKey: aiKeys.dataSources });
    },
  });
}

export function useDeleteAiKnowledgeDocument(userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => apiClient.delete(ENDPOINTS.AI_KNOWLEDGE_DELETE(documentId, userId ?? '')),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiKeys.knowledge });
      void qc.invalidateQueries({ queryKey: aiKeys.dataSources });
    },
  });
}

export function useAiInstructions(enabled = true) {
  return useQuery({
    queryKey: aiKeys.instructions,
    queryFn: () => apiClient.get<AiInstruction[]>(ENDPOINTS.AI_INSTRUCTIONS, { octo: { expectSuccess: false } }),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useSaveAiInstruction(userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<AiInstruction> & { content: string; title: string }) =>
      apiClient.post<AiInstruction>(ENDPOINTS.AI_INSTRUCTION_SAVE(userId ?? ''), {
        // Sunucu Guid ister: yeni kayıtta boş GUID.
        instructionId: body.instructionId ?? '00000000-0000-0000-0000-000000000000',
        ...body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiKeys.instructions });
    },
  });
}

export function useDeleteAiInstruction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (instructionId: string) => apiClient.delete(ENDPOINTS.AI_INSTRUCTION_DELETE(instructionId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiKeys.instructions });
    },
  });
}

// ---------- Kayıtlı AI raporları (sol menü "AI Raporları") ----------

/** Kullanıcının menüye kaydettiği AI raporları; menü grubu ve sekme aynı sorguyu paylaşır. */
export function useAiReports(userId: string | null, enabled = true) {
  return useQuery({
    queryKey: aiKeys.reports(userId ?? ''),
    queryFn: () => apiClient.get<AiReportSummary[]>(ENDPOINTS.AI_REPORTS(userId!), { octo: { expectSuccess: false } }),
    enabled: enabled && !!userId,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useAiReport(reportId: string | null) {
  return useQuery({
    queryKey: aiKeys.report(reportId ?? ''),
    queryFn: () => apiClient.get<AiReportSummary>(ENDPOINTS.AI_REPORT(reportId!), { octo: { expectSuccess: false } }),
    enabled: !!reportId,
    staleTime: 60_000,
    retry: false,
  });
}

/** Sohbetteki çıktıyı menüye kaydeder (içerik sunucuda kopyalanır). */
export function useSaveAiReport(userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ artifactId, title }: { artifactId: string; title?: string | null }) =>
      apiClient.post<AiReportSummary>(ENDPOINTS.AI_REPORTS_SAVE, { artifactId, userId, title: title ?? null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiKeys.reports(userId ?? '') });
    },
  });
}

export function useUpdateAiReport(userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      reportId,
      ...patch
    }: {
      reportId: string;
      title?: string;
      parameterDefaults?: Record<string, string>;
    }) => apiClient.put<AiReportSummary>(ENDPOINTS.AI_REPORT_UPDATE(reportId, userId ?? ''), patch),
    onSuccess: (data, vars) => {
      qc.setQueryData(aiKeys.report(vars.reportId), data);
      void qc.invalidateQueries({ queryKey: aiKeys.reports(userId ?? '') });
    },
  });
}

export function useDeleteAiReport(userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reportId: string) => apiClient.delete(ENDPOINTS.AI_REPORT(reportId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiKeys.reports(userId ?? '') });
    },
  });
}

/** Raporu parametrelerle yeniden çalıştırır; sorgusuz raporda kaydedilen HTML döner. */
export function renderAiReport(reportId: string, parameters: Record<string, string>) {
  return apiClient.post<AiRenderResponse>(
    ENDPOINTS.AI_REPORT_RENDER(reportId),
    { parameters },
    { octo: { expectSuccess: false } },
  );
}

/** Çıktının ham içeriği (HTML metin ya da PDF/Excel ikili) — kart açılınca çekilir. */
export function fetchArtifactBlob(artifactId: string) {
  return apiClient.getBlob(ENDPOINTS.AI_ARTIFACT_CONTENT(artifactId));
}

export function fetchReportBlob(reportId: string) {
  return apiClient.getBlob(ENDPOINTS.AI_REPORT_CONTENT(reportId));
}

/**
 * Sunucuda Claude Code var mı?
 *
 * Kurulum başına sabit bir yanıt: uzun önbellekli, oturum boyunca bir kez sorulur.
 * Yalnızca yetkisi olan kullanıcıya sorulur — yetkisizde istek bile atılmaz.
 */
export function useAiStatus(enabled: boolean) {
  return useQuery({
    queryKey: aiKeys.status,
    queryFn: () => apiClient.get<AiStatus>(ENDPOINTS.AI_STATUS, { octo: { expectSuccess: false } }),
    enabled,
    staleTime: 30 * 60_000,
    retry: false,
  });
}

/** Token kotası (merkezî havuz). Panel açılınca ve her yanıttan sonra tazelenir. */
export function useAiQuota(userId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: aiKeys.quota(userId ?? ''),
    queryFn: () => apiClient.get<AiQuota>(ENDPOINTS.AI_QUOTA(userId!), { octo: { expectSuccess: false } }),
    enabled: enabled && !!userId,
    staleTime: 30_000,
    retry: false,
  });
}

export function useAiConversations(userId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: aiKeys.conversations(userId ?? ''),
    queryFn: () => apiClient.get<AiConversation[]>(ENDPOINTS.AI_CONVERSATIONS(userId!)),
    enabled: enabled && !!userId,
    staleTime: 60_000,
  });
}

export function useAiMessages(conversationId: string | null) {
  return useQuery({
    queryKey: aiKeys.messages(conversationId ?? ''),
    queryFn: () => apiClient.get<AiMessageDto[]>(ENDPOINTS.AI_CONVERSATION_MESSAGES(conversationId!)),
    enabled: !!conversationId,
    staleTime: 60_000,
  });
}

export function useRenameConversation(userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, title }: { conversationId: string; title: string }) =>
      apiClient.put(ENDPOINTS.AI_CONVERSATION_RENAME(conversationId), { title }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiKeys.conversations(userId ?? '') });
    },
  });
}

export function useDeleteConversation(userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) =>
      apiClient.delete(ENDPOINTS.AI_CONVERSATION_DELETE(conversationId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiKeys.conversations(userId ?? '') });
    },
  });
}

export interface AiUploadResult extends Partial<AiAttachmentRef> {
  success?: boolean;
  message?: string;
  [key: string]: unknown;
}

/**
 * Dosya eki: içerik base64 olarak gönderilir, sunucu diske yazıp yolunu döner —
 * Claude o yolu okuyor (`--add-dir` + Read izni).
 */
export async function uploadAiAttachment(file: File): Promise<AiAttachmentRef> {
  const base64 = await fileToBase64(file);
  const res = await apiClient.post<AiUploadResult>(
    ENDPOINTS.AI_UPLOAD_ATTACHMENT,
    // Sunucu sözleşmesi (AiUploadAttachmentRequest): fileName + base64Content + contentType.
    {
      fileName: file.name,
      base64Content: base64,
      contentType: file.type || 'application/octet-stream',
    },
    { octo: { expectSuccess: false } },
  );
  if (res?.success === false || !res?.filePath) {
    throw new Error(res?.message || 'Dosya yüklenemedi.');
  }
  return {
    fileName: res.fileName ?? file.name,
    filePath: res.filePath,
    contentType: res.contentType ?? file.type ?? 'application/octet-stream',
    fileSize: res.fileSize ?? file.size,
  };
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('Dosya okunamadı.'));
    reader.readAsDataURL(file);
  });
}
